import React, { useEffect } from "react"
import { useGetAlpsQuery, useUpdateAlpsMutation } from "services/alps"
import { TextInput, Loading } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import { SubmitHandler, useForm } from "react-hook-form"
import useCommon from "hooks/useCommon"
import { MARKET_MODULE } from "constants/constants"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

type Inputs = {
  years: number
  pricePercentage: number
  lastMonth: number
  kcalPercentage: number
}

const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "alps",
}

export default function AlpsPage() {
  const { showSnackBar } = useCommon()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<Inputs>()

  const { data: alps, isFetching: isLoadingAlps } = useGetAlpsQuery()
  const [updateAlps, { isLoading: isLoadingEdit }] = useUpdateAlpsMutation()

  const onSubmit: SubmitHandler<Inputs> = async data => {
    try {
      await updateAlps(data).unwrap()
      showSnackBar("ALPS updated successfully!")
    } catch (error) {
      showSnackBar("ALPS not updated", "error")
    }
  }

  useEffect(() => {
    if (alps) {
      setValue("years", alps?.years)
      setValue("pricePercentage", alps?.pricePercentage)
      setValue("lastMonth", alps?.lastMonth)
      setValue("kcalPercentage", alps?.kcalPercentage)
    }
  }, [alps])

  return (
    <div className="mt-2">
      <h2>ALPS Settings</h2>
      {isLoadingEdit && <Loading />}
      {isLoadingAlps ? (
        <Loading withOverlay={false} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-3">
          <div className="d-flex align-center">
            <p style={{ minWidth: "600px" }}>Minimum number of years of continuous price series:</p>
            <div className="d-flex align-center">
              <TextInput
                className="mr-1"
                style={{ width: "7rem", textAlign: "center" }}
                type="numeric"
                defaultValue={alps?.years}
                invalid={errors.years}
                invalidText={errors.years?.message}
                {...register("years", { required: "Years is required" })}
              />
              <p>years</p>
            </div>
          </div>

          <div className="d-flex mt-3 align-center">
            <p style={{ minWidth: "600px" }}>
              Minimum percentage of price observations available for entire time series:
            </p>
            <div className="d-flex align-center">
              <TextInput
                className="mr-1"
                style={{ width: "7rem", textAlign: "center" }}
                type="numeric"
                defaultValue={alps?.pricePercentage}
                invalid={errors.pricePercentage}
                invalidText={errors.pricePercentage?.message}
                {...register("pricePercentage", { required: "Minimum percentage is required" })}
              />
              <p>%</p>
            </div>
          </div>

          <div className="d-flex mt-3 align-center">
            <p style={{ minWidth: "600px" }}>Last month of available data is maximum __ months before today:</p>
            <div className="d-flex align-center">
              <TextInput
                className="mr-1"
                style={{ width: "7rem", textAlign: "center" }}
                type="numeric"
                defaultValue={alps?.lastMonth}
                invalid={errors.lastMonth}
                invalidText={errors.lastMonth?.message}
                {...register("lastMonth", { required: "Last month is required" })}
              />
              <p>months</p>
            </div>
          </div>

          <div className="d-flex mt-3 align-center">
            <p style={{ minWidth: "600px" }}>Minimum percentage of kcal contribution of commodify to food basket:</p>
            <div className="d-flex align-center">
              <TextInput
                className="mr-1"
                style={{ width: "7rem", textAlign: "center" }}
                type="numeric"
                defaultValue={alps?.kcalPercentage}
                invalid={errors.kcalPercentage}
                invalidText={errors.kcalPercentage?.message}
                {...register("kcalPercentage", { required: "Kcal is required" })}
              />
              <p>%</p>
            </div>
          </div>

          <PermissionWrapper {...permissionsProps} feature="edit">
            <Button label="Save" action={handleSubmit(onSubmit)} className="btn-full green mt-4" />
          </PermissionWrapper>
        </form>
      )}
    </div>
  )
}
